import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import MediaCard from "@/components/feature/MediaCard";
import { searchMedia } from "@/lib/media";
import { newsList } from "@/mocks/news";
import { specials } from "@/mocks/specials";
import type { NewsItem } from "@/types/media";
import { usePageMeta } from "@/hooks/usePageMeta";

type TabKey = "all" | "movie" | "tv" | "news";

const tabs: { key: TabKey; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
  { key: "news", label: "资讯" },
];

const hotKeywords = ["深海", "悬疑", "科幻", "都市", "古装", "治愈"];

export default function Search() {
  usePageMeta({
    title: "搜索结果-555电影网",
    description: "在555电影网搜索电影、电视剧与影视资讯，支持按片名、演员、类型等关键词模糊匹配。",
    keywords: "影视搜索,电影搜索,电视剧搜索,555电影网",
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q")?.trim() ?? "";
  const [input, setInput] = useState(query);
  const [tab, setTab] = useState<TabKey>("all");

  useEffect(() => {
    setInput(query);
    setTab("all");
  }, [query]);

  const mediaResults = useMemo(() => {
    if (!query) return [];
    const type = tab === "movie" || tab === "tv" ? tab : "all";
    return searchMedia(query, type);
  }, [query, tab]);

  const newsResults = useMemo<NewsItem[]>(() => {
    if (!query) return [];
    if (tab === "movie" || tab === "tv") return [];
    const kw = query.toLowerCase();
    return newsList.filter((news) =>
      [news.title, news.summary, news.category, news.paragraphs.join(" ")]
        .join(" ")
        .toLowerCase()
        .includes(kw),
    );
  }, [query, tab]);

  const totalCount = mediaResults.length + newsResults.length;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = input.trim();
    setSearchParams(value ? { q: value } : {});
  };

  return (
    <div className="mx-auto max-w-page px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索结果" }]} />

      <div className="mt-4 rounded-xl border border-background-200 bg-background-100 px-5 py-6">
        <form onSubmit={handleSubmit} className="flex flex-col gap-3 sm:flex-row">
          <div className="flex h-12 flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 focus-within:border-secondary-400">
            <i className="ri-search-line text-lg text-foreground-400"></i>
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="输入片名、演员、类型或资讯关键词"
              className="h-full flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </div>
          <button
            type="submit"
            className="h-12 whitespace-nowrap rounded-md bg-primary-600 px-8 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
          >
            搜索
          </button>
        </form>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-xs text-foreground-400">热门搜索：</span>
          {hotKeywords.map((word) => (
            <button
              key={word}
              type="button"
              onClick={() => setSearchParams({ q: word })}
              className="whitespace-nowrap rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-600 transition-colors hover:bg-secondary-100 hover:text-secondary-700"
            >
              {word}
            </button>
          ))}
        </div>
      </div>

      {query ? (
        <>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">
              关键词「<span className="font-medium text-secondary-600">{query}</span>」共找到{" "}
              <span className="font-semibold text-foreground-900">{totalCount}</span> 条结果
            </p>
            <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
              {tabs.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setTab(item.key)}
                  className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                    tab === item.key
                      ? "bg-background-50 font-medium text-primary-700 shadow-sm"
                      : "text-foreground-500 hover:text-secondary-600"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {totalCount === 0 ? (
            <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16">
              <div className="flex h-16 w-16 items-center justify-center text-foreground-300">
                <i className="ri-search-eye-line text-4xl"></i>
              </div>
              <p className="mt-4 text-sm text-foreground-600">
                没有找到与「{query}」相关的内容
              </p>
              <p className="mt-1 text-xs text-foreground-400">
                试试更换关键词，或浏览下面的热门推荐
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link
                  to="/category/movie"
                  className="whitespace-nowrap rounded-md bg-secondary-500 px-5 py-2 text-sm text-background-50 transition-colors hover:bg-secondary-600"
                >
                  去电影频道看看
                </Link>
                <Link
                  to="/category/tv"
                  className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2 text-sm text-foreground-600 transition-colors hover:border-secondary-300 hover:text-secondary-600"
                >
                  去电视剧频道
                </Link>
              </div>
            </div>
          ) : (
            <div className="mt-6 space-y-10">
              {mediaResults.length > 0 && (
                <section>
                  <h2 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-foreground-900">
                    <span className="h-4 w-1 rounded-full bg-accent-500"></span>
                    影视作品（{mediaResults.length}）
                  </h2>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                    {mediaResults.map((item) => (
                      <MediaCard key={item.id} item={item} />
                    ))}
                  </div>
                </section>
              )}

              {newsResults.length > 0 && (
                <section>
                  <h2 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-foreground-900">
                    <span className="h-4 w-1 rounded-full bg-secondary-500"></span>
                    影视资讯（{newsResults.length}）
                  </h2>
                  <div className="space-y-3">
                    {newsResults.map((news) => (
                      <Link
                        key={news.id}
                        to={`/news/detail/${news.id}`}
                        className="group flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-secondary-300 sm:flex-row sm:items-center"
                      >
                        <div className="h-24 w-full shrink-0 overflow-hidden rounded bg-background-200 sm:h-20 sm:w-32">
                          <img
                            src={news.cover}
                            alt={`${news.title} 配图`}
                            title={`${news.title} - 555电影网`}
                            className="h-full w-full object-top"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-foreground-900 transition-colors group-hover:text-secondary-600">
                            {news.title}
                          </p>
                          <p className="mt-1 line-clamp-2 text-sm text-foreground-500">
                            {news.summary}
                          </p>
                          <p className="mt-1.5 text-xs text-foreground-400">
                            {news.category} · {news.date}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              <section className="rounded-lg bg-background-100 px-5 py-5">
                <h2 className="font-heading text-sm font-semibold text-foreground-900">
                  换个关键词试试
                </h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {hotKeywords.map((word) => (
                    <button
                      key={word}
                      type="button"
                      onClick={() => setSearchParams({ q: word })}
                      className="whitespace-nowrap rounded-full bg-background-50 px-3 py-1 text-xs text-foreground-600 transition-colors hover:text-secondary-600"
                    >
                      {word}
                    </button>
                  ))}
                </div>
              </section>
            </div>
          )}
        </>
      ) : (
        <div className="mt-10">
          <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-14">
            <div className="flex h-16 w-16 items-center justify-center text-foreground-300">
              <i className="ri-search-line text-4xl"></i>
            </div>
            <p className="mt-4 text-sm text-foreground-600">
              输入关键词，搜索电影、电视剧与影视资讯
            </p>
          </div>

          <section className="mt-10">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-foreground-900">
              <span className="h-4 w-1 rounded-full bg-accent-500"></span>
              热门专题推荐
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {specials.map((special) => (
                <Link
                  key={special.id}
                  to="/special"
                  className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-accent-300"
                >
                  <div className="h-32 w-full overflow-hidden bg-background-200">
                    <img
                      src={special.cover}
                      alt={`${special.title} 专题封面`}
                      title={`${special.title} - 555电影网`}
                      className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <p className="line-clamp-2 p-3 text-sm font-medium text-foreground-800 transition-colors group-hover:text-secondary-600">
                    {special.title}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}